<script lang="ts">
  /**
   * Config — how the household is set up, and the parts of it you may change.
   *
   * The screen draws a hard line between the two, because conflating them is how a
   * settings page becomes dangerous:
   *
   *   Editable   appearance, and per-portal rights and ordering. Household
   *              preferences and access control — things a person decides.
   *   Read-only  deployment facts: managed projects, the scheduler allowlist,
   *              which secrets exist. A form that could edit those could lock the
   *              platform out of its own estate, and an empty scheduler allowlist
   *              fails shut by design.
   *
   * Appearance is **published, not applied**: Fabric stores the estate's theme and
   * favicon and serves them at `GET /config`; each portal reads them at boot.
   * Fabric cannot reach into another app's DOM, and a screen that implied
   * otherwise would be worse than one that states the boundary.
   */
  import { Icon, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { stepUp } from "../lib/stepup.svelte";

  type PortalCfg = {
    id: string;
    name: string;
    url: string;
    minRole: "owner" | "partner" | "display" | "guest";
    status: string;
    enabled: boolean;
    order: number;
    projectId: string;
    theme: string | null;
    favicon: string | null;
  };
  type Deployment = {
    readOnly: boolean;
    note: string;
    fabricProjectId: string;
    managedProjects: string[];
    schedulerLocation: string;
    expectedSchedulerJobs: string[];
    programmeRepos: string[];
    household: string;
    frontDoorBaseUrl: string;
    extraAllowedOrigins: string[];
    billingAccountId: string | null;
    billingDataset: string;
    homeDataset: string;
    errorsDataset: string;
    haDigestKeySet: boolean;
    githubTokenSet: boolean;
    firestoreRulesShaSet: boolean;
    moduleServiceAccounts: { email: string; source: string }[];
    schedulerServiceAccounts: string[];
  };
  type Cfg = {
    appearance: { theme: string; favicon: string; accent: string | null; themes: string[]; updatedAt: string | null };
    portals: PortalCfg[];
    deployment: Deployment | null;
  };

  const ROLES = ["owner", "partner", "display", "guest"] as const;

  let cfg = $state<Cfg | null>(null);
  let error = $state<string | null>(null);
  let loading = $state(true);
  let busy = $state<string | null>(null);
  let toast = $state<{ kind: "ok" | "warn"; text: string } | null>(null);

  let theme = $state("stone");
  let favicon = $state("");

  async function load() {
    loading = true;
    try {
      cfg = await api.get<Cfg>("/config");
      theme = cfg.appearance.theme;
      favicon = cfg.appearance.favicon;
      error = null;
    } catch (e) {
      error = e instanceof ApiError ? e.message : "Could not load configuration";
    } finally {
      loading = false;
    }
  }

  $effect(() => {
    void load();
  });

  async function saveAppearance() {
    busy = "appearance";
    try {
      const ok = await stepUp.guard(
        () => api.patch("/config/appearance", { theme, favicon }),
        "Changing the estate's appearance",
      );
      toast = ok
        ? { kind: "ok", text: "Appearance saved. Each portal picks it up on its next load." }
        : { kind: "warn", text: "Cancelled." };
      if (ok) await load();
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not save" };
    } finally {
      busy = null;
    }
  }

  async function savePortal(p: PortalCfg) {
    busy = p.id;
    try {
      const ok = await stepUp.guard(
        () =>
          api.patch(`/config/portal/${p.id}`, {
            minRole: p.minRole,
            enabled: p.enabled,
            order: Number(p.order),
            theme: p.theme || null,
            favicon: p.favicon || null,
          }),
        `Changing who can reach ${p.name}`,
      );
      toast = ok ? { kind: "ok", text: `${p.name} updated.` } : { kind: "warn", text: "Cancelled." };
      if (ok) await load();
    } catch (e) {
      toast = { kind: "warn", text: e instanceof ApiError ? e.message : "Could not save" };
    } finally {
      busy = null;
    }
  }

  /** Live preview: apply the chosen theme to this document while deciding. */
  $effect(() => {
    if (theme) document.documentElement.setAttribute("data-theme", theme);
  });
</script>

<div class="wrap">
  {#if toast}
    <div class="attn" class:attn--warn={toast.kind === "warn"} role="status">
      <span class="attn__badge" aria-hidden="true"><Icon name={toast.kind === "warn" ? "alert" : "check"} size={12} /></span>
      <span class="attn__text">{toast.text}</span>
    </div>
  {/if}

  {#if loading}
    <section class="card"><p class="sub pad">Loading…</p></section>
  {:else if error}
    <section class="card"><p class="sub pad"><Icon name="alert" size={12} /> {error}</p></section>
  {:else if cfg}
    <!-- ── Appearance ──────────────────────────────────────────────────── -->
    <section class="card">
      <div class="sec">
        <span class="kicker">Appearance</span>
        <span class="sub">
          {#if cfg.appearance.updatedAt}changed {dateZA(cfg.appearance.updatedAt)}{/if}
        </span>
      </div>

      <div class="form">
        <label>
          <span class="lb">Theme</span>
          <select bind:value={theme}>
            {#each cfg.appearance.themes as t (t)}<option value={t}>{t}</option>{/each}
          </select>
        </label>
        <label>
          <span class="lb">Favicon</span>
          <input bind:value={favicon} placeholder="🎬 or https://…" maxlength="200" />
        </label>
        <button class="btn-primary" disabled={busy === "appearance"} onclick={saveAppearance}>
          {busy === "appearance" ? "Saving…" : "Save appearance"}
        </button>
      </div>

      <p class="sub foot">
        <Icon name="alert" size={12} />
        Themes set only the neutral surface and text ramp. The copper accent and the
        blue/amber status colours are fixed in every theme, because they encode meaning —
        a theme switch must never change what a colour means.
      </p>
      <p class="sub foot">
        <Icon name="pause" size={12} />
        <strong>Published, not applied.</strong> Fabric stores this and serves it at
        <code>GET /config</code>; each portal reads it at boot. Fabric cannot restyle another
        app directly, so a portal only changes on its next load — and only once it consumes
        the setting.
      </p>
    </section>

    <!-- ── Rights per portal ───────────────────────────────────────────── -->
    <section class="card">
      <div class="sec">
        <span class="kicker">Access per portal</span>
        <span class="sub">{cfg.portals.length} registered</span>
      </div>

      <p class="sub foot">
        <Icon name="shield" size={12} />
        <strong>Minimum role</strong> is the control that matters. It is enforced three times
        over: by the registry read every portal list uses, by the cross-portal search fan-out,
        and again in <code>firestore.rules</code>. Lowering it widens who can reach a portal at all.
      </p>

      <div class="scroll">
        <table>
          <thead>
            <tr><th>Portal</th><th>Address</th><th>Minimum role</th><th>Order</th><th>On</th><th>Theme</th><th></th></tr>
          </thead>
          <tbody>
            {#each cfg.portals as p (p.id)}
              <tr class:off={!p.enabled}>
                <td>
                  <div class="nm">{p.name}</div>
                  <div class="sub num">{p.projectId}</div>
                </td>
                <td class="sub num">{p.url.replace(/^https:\/\//, "")}</td>
                <td>
                  <select bind:value={p.minRole} aria-label="Minimum role for {p.name}">
                    {#each ROLES as r (r)}<option value={r}>{r}</option>{/each}
                  </select>
                </td>
                <td><input class="ord num" type="number" bind:value={p.order} aria-label="Order for {p.name}" /></td>
                <td>
                  <label class="tog">
                    <input type="checkbox" bind:checked={p.enabled} />
                    {#if p.enabled}
                      <span class="status status--ok"><Icon name="check" size={12} /> on</span>
                    {:else}
                      <span class="status status--idle"><Icon name="pause" size={12} /> off</span>
                    {/if}
                  </label>
                </td>
                <td>
                  <select bind:value={p.theme} aria-label="Theme override for {p.name}">
                    <option value={null}>inherit</option>
                    {#each cfg.appearance.themes as t (t)}<option value={t}>{t}</option>{/each}
                  </select>
                </td>
                <td>
                  <button class="btn-primary" disabled={busy === p.id} onclick={() => savePortal(p)}>
                    {busy === p.id ? "Saving…" : "Save"}
                  </button>
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </section>

    <!-- ── Deployment, read-only ───────────────────────────────────────── -->
    {#if cfg.deployment}
      {@const d = cfg.deployment}
      <section class="card">
        <div class="sec"><span class="kicker">Deployment</span><span class="sub">read-only</span></div>
        <p class="sub foot"><Icon name="alert" size={12} /> {d.note}</p>

        <div class="facts">
          {@render fact("Fabric project", d.fabricProjectId)}
          {@render fact("Household", d.household)}
          {@render fact("Front Door", d.frontDoorBaseUrl.replace(/^https:\/\//, ""))}
          {@render fact("Managed projects", d.managedProjects.join(", "))}
          {@render fact("Scheduler location", d.schedulerLocation)}
          {@render fact("Expected jobs", d.expectedSchedulerJobs.join(", "))}
          {@render fact("Programme repos", d.programmeRepos.join(", "))}
          {@render fact("Billing account", d.billingAccountId ?? "not set")}
          {@render fact("Datasets", `${d.homeDataset} · ${d.billingDataset} · ${d.errorsDataset}`)}
          {@render fact("Extra CORS origins", d.extraAllowedOrigins.join(", ") || "none")}
          {@render fact("Module identities", d.moduleServiceAccounts.map((m) => `${m.source}`).join(", ") || "none")}
          {@render fact("Scheduler identities", d.schedulerServiceAccounts.join(", ") || "none — nothing may trigger jobs")}
        </div>

        <!-- Presence, never the value. A config screen has no business displaying
             a credential; whether one exists is the useful fact. -->
        <div class="secrets">
          {@render presence("HA digest key", d.haDigestKeySet)}
          {@render presence("GitHub read-only token", d.githubTokenSet)}
          {@render presence("Firestore rules digest (CI)", d.firestoreRulesShaSet)}
        </div>
      </section>
    {/if}
  {/if}
</div>

{#snippet fact(label: string, value: string)}
  <div class="fact">
    <div class="lb">{label}</div>
    <div class="fact__v">{value}</div>
  </div>
{/snippet}

{#snippet presence(label: string, set: boolean)}
  <span class="pchip">
    {#if set}
      <span class="status status--ok"><Icon name="check" size={12} /> {label}</span>
    {:else}
      <span class="status status--idle"><Icon name="pause" size={12} /> {label} not set</span>
    {/if}
  </span>
{/snippet}

<style>
  .wrap { display: flex; flex-direction: column; gap: 14px; }
  .card { padding: 13px 15px 15px; }
  .sec { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding-bottom: 9px; }
  .pad { padding: 6px 0 2px; display: flex; align-items: center; gap: 6px; }
  .foot { margin: 9px 0 0; display: flex; align-items: flex-start; gap: 6px; }
  code { font-size: 0.72rem; color: var(--acc); }
  .scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.7812rem; }
  th { text-align: left; font-size: 0.72rem; font-weight: 700; color: var(--mut); padding: 6px 8px;
       border-bottom: 1px solid var(--line); white-space: nowrap; }
  td { padding: 8px; border-bottom: 1px solid var(--line); vertical-align: middle; }
  tbody tr:last-child td { border-bottom: none; }
  tr.off { opacity: 0.55; }
  .nm { font-weight: 700; color: var(--tx); }
  select, input { background: var(--fill); color: var(--tx); border: 1px solid var(--line);
                  border-radius: var(--r-control); padding: 5px 7px; font-size: 0.75rem; }
  .ord { width: 62px; }
  .tog { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; }
  button:disabled { opacity: 0.5; cursor: default; }

  .form { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; padding: 2px 0 6px; }
  .form label { display: flex; flex-direction: column; gap: 4px; }

  .facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 10px;
           margin-top: 12px; padding-top: 10px; border-top: 1px solid var(--line); }
  .fact__v { font-size: 0.75rem; color: var(--tx); overflow-wrap: anywhere; }
  .secrets { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; padding-top: 10px;
             border-top: 1px solid var(--line); }
  .pchip { font-size: 0.72rem; }
</style>
